import React, { useEffect, useState } from 'react';
import {
    Paper, Typography, Table, TableHead, TableRow,
    TableCell, TableBody, IconButton, Box, Grid, TextField,
    Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { setupInterceptorsTo } from "../../Componentes/API/AxiosBuilder";
import axios from "axios";
import { RUTA_API_SUELDOS } from "../../Contantes/Constantes";
import { useBody } from "../../Body/state/BodyContext";

const TabHistorico = () => {
    const axiosInstance = setupInterceptorsTo(axios.create());
    const [archivos, setArchivos] = useState([]);
    const [filtroConvenio, setFiltroConvenio] = useState("");
    const [filtroFechaTexto, setFiltroFechaTexto] = useState("");
    const { setMessage } = useBody();

    useEffect(() => {
        const fetchArchivos = async () => {
            try {
                const response = await axiosInstance.get(RUTA_API_SUELDOS + '/archivos');
                setArchivos(response.data);
            } catch (error) {
                setMessage("Error al obtener archivos")
                console.error("Error al obtener archivos:", error);
            }
        };
        fetchArchivos();
    }, []);

    const handleDescarga = async (nombreArchivo) => {
        try {
            const response = await axiosInstance({
                method: 'GET',
                url: `${RUTA_API_SUELDOS}/${nombreArchivo}/descargar`,
                responseType: "blob",
            });

            const url = window.URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = nombreArchivo;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            setMessage("Hubo un error al descargar el archivo (Codigo de error: 00031)");
        }
    };

    const getFechaFromNombre = (nombre) => {
        const match = nombre.match(/_(\d{8})_/);
        if (!match) return null;

        const fecha = match[1];
        const anio = parseInt(fecha.substring(0, 4));
        const mes = parseInt(fecha.substring(4, 6)) - 1;
        const dia = parseInt(fecha.substring(6, 8));

        return new Date(anio, mes, dia);
    };

    const formatFecha = (fecha) => {
        if (!(fecha instanceof Date) || isNaN(fecha)) return "Fecha inválida";
        const pad = (n) => n.toString().padStart(2, '0');
        return `${pad(fecha.getDate())}/${pad(fecha.getMonth() + 1)}/${fecha.getFullYear()}`;
    };

    const archivosFiltradosYOrdenados = archivos
        .map(nombre => {
            const fechaObj = getFechaFromNombre(nombre);
            const partes = nombre.split('_');

            const convenio = partes[0] || "";
            const cantSueldos = parseInt(partes[2], 10) || 0;
            const montoTotal = parseFloat(partes[3]) || 0;

            return {
                nombre,
                fechaObj,
                fechaTexto: formatFecha(fechaObj),
                convenio,
                cantSueldos,
                montoTotal
            };
        })
        .filter(({ fechaObj, convenio }) => {
            const coincideConvenio = convenio.includes(filtroConvenio.trim());

            let coincideFecha = true;
            if (filtroFechaTexto.trim() !== "") {
                const fechaTexto = filtroFechaTexto.trim();
                const partes = fechaTexto.split(/[\/\-\.]/).map(p => parseInt(p, 10)).filter(n => !isNaN(n));

                const fecha = dayjs(fechaObj);
                const dia = fecha.date();
                const mes = fecha.month() + 1;
                const anio = fecha.year();

                if (partes.length === 1) {
                    coincideFecha = anio === partes[0];
                } else if (partes.length === 2) {
                    if (partes[0] > 12) {
                        coincideFecha = dia === partes[0] && mes === partes[1];
                    } else {
                        coincideFecha = mes === partes[0] && anio === partes[1];
                    }
                } else if (partes.length === 3) {
                    coincideFecha = dia === partes[0] && mes === partes[1] && anio === partes[2];
                } else {
                    coincideFecha = false;
                }
            }

            return coincideConvenio && coincideFecha;
        })
        .sort((a, b) => b.fechaObj - a.fechaObj);

    const archivosPorAnio = archivosFiltradosYOrdenados.reduce((acc, archivo) => {
        const anio = archivo.fechaObj.getFullYear();
        if (!acc[anio]) acc[anio] = [];
        acc[anio].push(archivo);
        return acc;
    }, {});

    const aniosOrdenados = Object.keys(archivosPorAnio).sort((a, b) => b - a);

    return (
        <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
                Archivos históricos de sueldos
            </Typography>

            <Box sx={{ mb: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Filtrar por número de convenio"
                            variant="outlined"
                            value={filtroConvenio}
                            onChange={(e) => setFiltroConvenio(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Filtrar por fecha (día/mes/año)"
                            variant="outlined"
                            value={filtroFechaTexto}
                            onChange={(e) => setFiltroFechaTexto(e.target.value)}
                            placeholder="Ej: 2023, 03/2024, 15/04, 15/04/2023"
                        />
                    </Grid>
                </Grid>
            </Box>

            {archivosFiltradosYOrdenados.length === 0 ? (
                <Typography variant="body1" sx={{ mt: 2, textAlign: 'center' }}>
                    No hay archivos históricos
                </Typography>
            ) : (
                aniosOrdenados.map(anio => (
                    <Accordion key={anio}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle1"><strong>Año {anio}</strong></Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Nombre</strong></TableCell>
                                        <TableCell><strong>Fecha de generación</strong></TableCell>
                                        <TableCell><strong>Cantidad de registros</strong></TableCell>
                                        <TableCell><strong>Monto total</strong></TableCell>
                                        <TableCell><strong>Acciones</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {archivosPorAnio[anio].map(({ nombre, fechaTexto, cantSueldos, montoTotal }) => (
                                        <TableRow key={nombre}>
                                            <TableCell>{nombre}</TableCell>
                                            <TableCell>{fechaTexto}</TableCell>
                                            <TableCell>{cantSueldos}</TableCell>
                                            <TableCell>${montoTotal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</TableCell>
                                            <TableCell>
                                                <IconButton onClick={() => handleDescarga(nombre)}>
                                                    <DownloadIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </AccordionDetails>
                    </Accordion>
                ))
            )}

        </Paper>
    );
};

export default TabHistorico;
